'use client';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import Close from '../../../public/images/x.svg';
import { NormalButton } from '../../theme/components';
import Modal from '../../../components/Modal';
import ShiberseLoader from '../../../components/Loader/loader';
import { formatFromBalance, shortenDouble } from '../../../utils';
import useShiberseLandAuction from '../../../hooks/useShiberseLandAuction';
import useIsTransactionPending from '../../../hooks/useIsTransactionPending';
import { useAccount, useBalance } from 'wagmi';
import Loader from '../../../components/Loader';
import { Events } from '../../../constants/map';
import useShibaSwapTokenBalance from '../../../hooks/useShibaSwapTokenBalance';
import { Dots } from '../../../components/Pool/styleds';
import Image from 'next/image';
import { ethers } from 'ethers';
import useLandMap from '../../../hooks/useLandMap';
import { usePathname } from 'next/navigation';
import { useUserInfoContext } from '../../../contexts/userinfo-context';
import { mainNetworkChainId, sepoliaChainId } from '../../../constants';
import {
  useShiberseLandSaleMainnetContract,
  useShiberseLandSaleSeploliaContract,
} from '../../../hooks/useContract';

const UpperSection = styled.div`
  position: relative;
  h5 {
    margin: 0;
    margin-bottom: 0.5rem;
    font-size: 1rem;
    font-weight: 500;
  }

  h5:last-child {
    margin-bottom: 0px;
  }

  h4 {
    margin-top: 0;
    font-weight: 500;
  }
`;
const mediaWidths = {
  upToExtra2Small: 320,
  upToExtraSmall: 500,
  upToSmall: 720,
  upToMedium: 960,
  upToLarge: 1280,
};
const mediaQuery = (key: keyof typeof mediaWidths) => {
  return `@media (max-width: ${mediaWidths[key]}px)`;
};

const ConnectWalletWrapper = styled.div`
  padding: 1rem;
  text-align: center;
  background-color: rgba(0, 0, 0, 0.7);
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  ${({ theme }) => mediaQuery('upToExtraSmall')} {
    padding: 0;
  }
`;

const CloseIcon = styled.div`
  position: absolute;
  right: 0.5rem;
  top: 0;
  padding: 0.5rem;
  z-index: 10;
  &:hover {
    cursor: pointer;
    opacity: 0.6;
  }
`;

const CloseColor = styled(Close)`
  path {
    stroke: #fff;
  }
`;

const HeaderText = styled.div`
  padding: 1rem 0;

  :hover {
    cursor: pointer;
  }

  ${mediaQuery('upToExtraSmall')} {
    font-size: 1.5rem !important;
    line-height: 1.7rem !important;
    margin-top: 1rem;
  }

  ${mediaQuery('upToExtra2Small')} {
    font-size: 1.3rem !important;
    line-height: 1.5rem !important;
    margin-top: 1rem;
  }
`;

const HeaderDescription = styled.div`
  color: rgb(255, 255, 255, 0.5);
  font-size: 14px;
  line-height: 21px;

  ${mediaQuery('upToExtraSmall')} {
    padding: 0;
  }

  ${mediaQuery('upToExtra2Small')} {
    font-size: 0.9rem !important;
  }
`;

const ContentWrapper = styled.div`
  padding: 1.5rem 1.5rem;
  background: #130f0fcc;
  border-radius: 24px;
  width: 460px;
  max-width: 100%;
  margin: 2rem 0;
`;

const BalanceWrapper = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  color: rgb(255, 255, 255, 0.5);
  margin: 1rem 0;
  margin-bottom: 0;
`;

const HeaderIcon = styled.div`
  padding: 2rem 0;
  padding-bottom: 0;

  img {
    width: 40px;
  }
`;

const MintWrapper = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #ffffff;
`;

const ValidationText = styled.p`
  font-size: 14px;
`;

export const MintModal = (props: any) => {
  const { address: account, chain } = useAccount(); // Use wagmi to get the account address
  const pathname = usePathname();
  const { refreshLands } = useUserInfoContext();
  // const userEthBalance = useETHBalances(account ? [account] : [])?.[account ?? '']
  // const currentBalance = parseFloat(userEthBalance?.toSignificant() as any)
  const { data: userEthBalance } = useBalance({
    address: account,
    // watch: true, // Optional: to keep the balance updated
  });

  const currentBalance = parseFloat(userEthBalance?.formatted ?? '0');

  const [validateText, setValidateText] = useState(null) as any;
  const [pendingTx, setPendingTx] = useState<string | null>(null);
  const [requestedApproval, setRequestedApproval] = useState(false);
  const { fetchAllLandInfo } = useLandMap();
  const isPending = useIsTransactionPending(pendingTx ?? undefined);

  const isConfirmedTx = pendingTx !== null && !isPending;

  const {
    currentStage,
    mintPublic,
    mintPublicWithShib,
    shibTokenAddress,
    shibAllowance,
    shibApprove,
    wethTokenAddress,
    wethAllowance,
    wethApprove,
    mintWinningBid,
  } = useShiberseLandAuction({});

  const shibaBalanceBigInt = useShibaSwapTokenBalance(shibTokenAddress ? shibTokenAddress : '');
  const shibaBalanceValue = parseFloat(
    formatFromBalance(shibaBalanceBigInt?.value, shibaBalanceBigInt?.decimals)
  );
  const wethBalanceBigInt = useShibaSwapTokenBalance(wethTokenAddress ? wethTokenAddress : '');
  const wethBalanceValue = parseFloat(
    formatFromBalance(wethBalanceBigInt?.value, wethBalanceBigInt?.decimals)
  );
  const landSaleMainnetContract = useShiberseLandSaleMainnetContract(true);
  const landSaleSeploliaContract = useShiberseLandSaleSeploliaContract(true);

  // detect claim case: unclaimed winner and you are the winner
  const isClaim = !!(
    props?.landInfo?.bidWinner &&
    props?.landInfo?.bidWinner !== '0x0000000000000000000000000000000000000000' &&
    !props?.landInfo?.isMinted &&
    account &&
    props?.landInfo?.bidWinner?.toLowerCase() === account?.toLowerCase()
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!isConfirmedTx) return;
      if (isConfirmedTx) {
        try {
          if (pathname.includes('map')) {
            //await fetchAllLandInfo(); // Fetch land info if on the map page
            await refreshLands(); // Refresh lands after fetching
          }

          // Call the close action after a delay if it exists
          if (props.handleCloseAction) {
            const timeoutId = setTimeout(() => {
              props.handleCloseAction();
            }, 3000);

            // Cleanup function to clear the timeout if the component unmounts
            return () => clearTimeout(timeoutId);
          }
        } catch (error) {
          console.error('Error during fetchData:', error); // Improved error handling
        }
      }
      return;
    };

    fetchData();
  }, [isConfirmedTx]);

  useEffect(() => {
    if (props.isOpen) {
      setPendingTx(null);
      setValidateText(null);
    }
  }, [props.isOpen]);

  const handleMint = async () => {
    if (!isClaim) {
      if (props.mintType === 'eth' && Number(currentBalance) < Number(props.landInfo.price)) {
        setValidateText('Insufficient ETH balance!');
        return;
      }

      if (
        props.mintType === 'shib' &&
        Number(shibaBalanceValue) < Number(props.landInfo.shibPrice)
      ) {
        setValidateText('Insufficient SHIB balance!');
        return;
      }
      if (props.mintType === 'weth' && Number(wethBalanceValue) < Number(props.landInfo.ethPrice)) {
        setValidateText('Insufficient WETH balance!');
        return;
      }
    }

    setValidateText(null);
    let messageFee;
    try {
      const needsMessageFee = chain?.id === mainNetworkChainId || chain?.id === sepoliaChainId;
      if (needsMessageFee) {
        messageFee = await (chain?.id === mainNetworkChainId
          ? landSaleMainnetContract?.getMessageFee([[props.landInfo?.id, true]])
          : landSaleSeploliaContract?.getMessageFee([[props.landInfo?.id, true]]));
      }
    } catch (error) {
      console.error('Error fetching message fee:', error);
    }

    let tx;
    if (isClaim) {
      const mintBidWinnerData: any = {
        xArray: [props.landInfo?.coordinates?.x],
        yArray: [props.landInfo?.coordinates?.y],
      };
      if (messageFee) mintBidWinnerData.messageFee = messageFee;
      if (process.env.NODE_ENV !== 'production') {
        console.log('[claim] payload', {
          x: mintBidWinnerData.xArray[0],
          y: mintBidWinnerData.yArray[0],
          chainId: chain?.id,
          messageFee: messageFee?.toString?.() ?? null,
          account,
        });
      }
      tx = await mintWinningBid(mintBidWinnerData);
      if (process.env.NODE_ENV !== 'production') {
        console.log('[claim] response', tx);
      }
    } else if (currentStage === Events['Public']) {
      const inputData: any = {
        x: props.landInfo?.coordinates?.x,
        y: props.landInfo?.coordinates?.y,
        messageFee,
      };
      if (props.mintType === 'eth') {
        inputData.amount = props.landInfo?.bigNumbEthPrice;
        tx = await mintPublic(inputData);
      } else if (props.mintType === 'weth') {
        inputData.amount = props.landInfo?.bigNumbEthPrice;
        tx = await mintPublic(inputData);
      } else if (props.mintType === 'shib') {
        inputData.amount = props.landInfo?.bigNumbShibPrice;
        tx = await mintPublicWithShib(inputData);
      }
    }

    if (!tx.hash) {
      if (JSON.stringify(tx.message).includes('ERR_X_OUT_OF_RANGE')) {
        setValidateText('X coordinate out of range');
      }
      if (JSON.stringify(tx.message).includes('ERR_Y_OUT_OF_RANGE')) {
        setValidateText('Y coordinate out of range');
      }
      if (JSON.stringify(tx.message).includes('ERR_SALE_NOT_ACTIVE')) {
        setValidateText('Sale is not active');
      }
      if (JSON.stringify(tx.message).includes('ERR_NOT_UP_FOR_SALE')) {
        setValidateText('Land is not up for sale');
      }
      if (JSON.stringify(tx.message).includes('ERR_MINTED_ON_SHIBARIUM')) {
        setValidateText('Land already minted on Shibarium');
      }
      if (!isClaim && JSON.stringify(tx.message).includes('ERR_HAS_WINNING_BIDDER')) {
        setValidateText('Land already has a winning bidder');
      }
      if (JSON.stringify(tx.message).includes('ERR_INSUFFICIENT_PAYMENT')) {
        setValidateText('Not enough payment sent for purchase');
      }
      if (JSON.stringify(tx.message).includes('ERR_INVALID_PRICE_CONVERSION')) {
        setValidateText('Invalid price conversion');
      }
      if (JSON.stringify(tx.message).includes('ERR_INSUFFICIENT_AMOUNT_SENT')) {
        setValidateText('Amount sent is less than required');
      }
    }
    if (tx?.hash) setPendingTx(tx.hash);
  };
  const handleApprove = useCallback(async () => {
    try {
      let result;
      if (props.mintType === 'shib') {
        result = await shibApprove();
      } else if (props.mintType === 'weth') {
        result = await wethApprove();
      }

      if (result && result.success && result.txHash) {
        setRequestedApproval(true);
      } else if (result && !result.success) {
        console.log('Approval failed:', result.error);
      }
    } catch (e) {
      console.log('Unexpected error:', e);
    }
  }, [shibApprove, wethApprove, setRequestedApproval]);

  return (
    <Modal isOpen={props.isOpen} onDismiss={props.onDismiss} minheight={false} maxheight={80}>
      <UpperSection>
        <CloseIcon onClick={props.onDismiss}>
          <CloseColor />
        </CloseIcon>

        {!isConfirmedTx && !isPending ? (
          <ConnectWalletWrapper className="flex relative flex-col items-center">
            <>
              <HeaderText className="text-3xl text-center text-white">
                {isClaim ? 'Claim Land' : 'Mint Land'}
              </HeaderText>
              <HeaderDescription className="text-center text-white">
                {isClaim
                  ? 'You are about to claim your winning land.'
                  : 'You are about to mint for a Land.'}
              </HeaderDescription>
            </>

            <ContentWrapper className="relative">
              <MintWrapper className="flex justify-between w-full">
                <div>{isClaim ? 'Claim Land' : 'Mint Land'}</div>
                <div className="flex justify-center items-center">
                  ({`${props.landInfo.coordinates?.x}, ${props.landInfo.coordinates?.y}`})
                </div>
              </MintWrapper>

              <BalanceWrapper className="flex justify-between w-full">
                <span>
                  {isClaim
                    ? ''
                    : props.mintType === 'eth'
                      ? `${props.landInfo.price} ETH`
                      : props.mintType === 'weth'
                        ? `${props.landInfo.ethPrice} WETH`
                        : `${shortenDouble(Number(props.landInfo.shibPrice), 2)} SHIB`}
                </span>
                <span>
                  Current Balance:{' '}
                  {isClaim
                    ? chain?.id === mainNetworkChainId || chain?.id === sepoliaChainId
                      ? `${currentBalance} ETH`
                      : `${currentBalance} BONE`
                    : props.mintType === 'eth'
                      ? `${currentBalance} ETH`
                      : props.mintType === 'weth'
                        ? `${wethBalanceValue} WETH`
                        : `${shortenDouble(Number(shibaBalanceValue), 2)} SHIB`}
                </span>
              </BalanceWrapper>

              <ValidationText className={`text-red-500 ${!validateText ? 'hidden' : ''}`}>
                {validateText}
              </ValidationText>
            </ContentWrapper>

            {!isClaim &&
            props.mintType === 'shib' &&
            (!shibAllowance || Number(shibAllowance) === 0) ? (
              <NormalButton className="px-12" disabled={requestedApproval} onClick={handleApprove}>
                {!requestedApproval ? 'APPROVE' : <Dots>APPROVING</Dots>}
              </NormalButton>
            ) : !isClaim &&
              props.mintType === 'weth' &&
              (!wethAllowance || Number(wethAllowance) === 0) ? (
              <NormalButton className="px-12" disabled={requestedApproval} onClick={handleApprove}>
                {!requestedApproval ? 'APPROVE' : <Dots>APPROVING</Dots>}
              </NormalButton>
            ) : (
              <NormalButton className="px-12" onClick={handleMint}>
                {isClaim ? 'CLAIM' : 'MINT'}
              </NormalButton>
            )}
          </ConnectWalletWrapper>
        ) : isPending ? (
          <ConnectWalletWrapper className="flex relative flex-col items-center">
            <div className="my-4 mt-8">
              <ShiberseLoader size="30px" />
            </div>
            <>
              <HeaderText className="text-3xl text-center">Minting in progress</HeaderText>
              <HeaderDescription className="text-center text-white">
                This process can take few minutes.
              </HeaderDescription>
              <HeaderDescription className="mb-8 text-center text-white">
                Once transaction is over, it will be added to your wallet.
              </HeaderDescription>
            </>
          </ConnectWalletWrapper>
        ) : (
          <ConnectWalletWrapper className="flex relative flex-col items-center">
            <>
              <HeaderIcon>
                <Image
                  src={'/images/map/confirmIcon.svg'}
                  width={40}
                  height={40}
                  alt="confirm-icon"
                />
              </HeaderIcon>
              <HeaderText className="text-3xl text-center text-white">
                Minting Transaction Submitted
              </HeaderText>
              <HeaderDescription className="text-center text-white">WOOF!</HeaderDescription>
              <HeaderDescription className="mb-8 text-center text-white">
                Your land minting is in progress.
              </HeaderDescription>
            </>
          </ConnectWalletWrapper>
        )}
      </UpperSection>
    </Modal>
  );
};

export const MintMultiModal = (props: any) => {
  const { refreshLands } = useUserInfoContext();
  const { address: account, chain } = useAccount();
  const { data: userEthBalance } = useBalance({
    address: account,
  });
  const { fetchAllLandInfo } = useLandMap();
  const currentBalance = parseFloat(userEthBalance?.formatted ?? '0');
  const pathname = usePathname();
  const [validateText, setValidateText] = useState(null) as any;
  const [pendingTx, setPendingTx] = useState<string | null>(null);
  const [requestedApproval, setRequestedApproval] = useState(false);
  const landSaleMainnetContract = useShiberseLandSaleMainnetContract(true);
  const landSaleSeploliaContract = useShiberseLandSaleSeploliaContract(true);

  const [formattedTotalPrice, setFormattedTotalPrice] = useState<number | string>(0);
  const [totalPrice, setTotalPrice] = useState();
  const [totalFee, setTotalFee] = useState();
  const [isLoading, setIsLoading] = useState(true);

  const [priceArray, setPriceArray] = useState([]) as any;

  const isPending = useIsTransactionPending(pendingTx ?? undefined);

  const isConfirmedTx = pendingTx !== null && !isPending;
  const {
    fetchLandPrice,
    currentStage,
    mintPublicMulti,
    mintPublicWithShibMulti,
    shibTokenAddress,
    fetchLandShibPrice,
    shibAllowance,
    shibApprove,
    wethTokenAddress,
    wethAllowance,
    wethApprove,
  } = useShiberseLandAuction({});

  const shibaBalanceBigInt = useShibaSwapTokenBalance(shibTokenAddress ? shibTokenAddress : '');
  const shibaBalanceValue = parseFloat(
    formatFromBalance(shibaBalanceBigInt?.value, shibaBalanceBigInt?.decimals)
  );
  const shibaBalanceWETHBigInt = useShibaSwapTokenBalance(wethTokenAddress ? wethTokenAddress : '');
  const shibaBalanceWETHValue = parseFloat(
    formatFromBalance(shibaBalanceWETHBigInt?.value, shibaBalanceWETHBigInt?.decimals)
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!isConfirmedTx) return; // Early return if not confirmed

      try {
        if (pathname.includes('map')) {
          //await fetchAllLandInfo(); // Fetch land info if on the map page
          await refreshLands(); // Refresh lands after fetching
        }

        // Call the close action after a delay if it exists
        if (props.handleCloseAction) {
          const timeoutId = setTimeout(() => {
            props.handleCloseAction();
          }, 3000);

          // Cleanup function to clear the timeout if the component unmounts
          return () => clearTimeout(timeoutId);
        }
      } catch (error) {
        console.error('Error during fetchData:', error); // Improved error handling
      }
      return;
    };

    fetchData(); // Invoke the async function
  }, [isConfirmedTx]);

  const handleMint = async () => {
    if (props.mintType === 'eth' && Number(currentBalance) < Number(formattedTotalPrice)) {
      setValidateText('Insufficient ETH balance!');
      return;
    }

    if (props.mintType === 'shib' && Number(shibaBalanceValue) < Number(formattedTotalPrice)) {
      setValidateText('Insufficient SHIB balance!');
      return;
    }

    if (props.mintType === 'weth' && Number(shibaBalanceWETHValue) < Number(formattedTotalPrice)) {
      setValidateText('Insufficient WETH balance!');
      return;
    }

    setValidateText(null);
    const inputData = {
      totalAmount: totalPrice,
      xArray: [],
      yArray: [],
      priceArray: priceArray,
      messageFee: totalFee,
    } as any;

    for (let i = 0; i < props.selectedInfo.length; i++) {
      inputData.xArray.push(props.selectedInfo[i].coordinates.x);
      inputData.yArray.push(props.selectedInfo[i].coordinates.y);
    }

    let tx;
    if (currentStage === Events['Public']) {
      if (props.mintType === 'eth') tx = await mintPublicMulti(inputData);
      else if (props.mintType === 'shib') tx = await mintPublicWithShibMulti(inputData);
      else if (props.mintType === 'weth') tx = await mintPublicMulti(inputData);
      if (tx.hash) setPendingTx(tx.hash);
    }

    if (!tx.hash) {
      if (JSON.stringify(tx.message).includes('ERR_X_OUT_OF_RANGE')) {
        setValidateText('X coordinate out of range');
      }
      if (JSON.stringify(tx.message).includes('ERR_Y_OUT_OF_RANGE')) {
        setValidateText('Y coordinate out of range');
      }
      if (JSON.stringify(tx.message).includes('ERR_SALE_NOT_ACTIVE')) {
        setValidateText('Sale is not active');
      }
      if (JSON.stringify(tx.message).includes('ERR_NOT_UP_FOR_SALE')) {
        setValidateText('Land is not up for sale');
      }
      if (JSON.stringify(tx.message).includes('ERR_MINTED_ON_SHIBARIUM')) {
        setValidateText('Land already minted on Shibarium');
      }
      if (JSON.stringify(tx.message).includes('ERR_HAS_WINNING_BIDDER')) {
        setValidateText('Land already has a winning bidder');
      }
      if (JSON.stringify(tx.message).includes('ERR_INSUFFICIENT_PAYMENT')) {
        setValidateText('Not enough payment sent for purchase');
      }
      if (JSON.stringify(tx.message).includes('ERR_INVALID_PRICE_CONVERSION')) {
        setValidateText('Invalid price conversion');
      }
      if (JSON.stringify(tx.message).includes('ERR_INSUFFICIENT_AMOUNT_SENT')) {
        setValidateText('Amount sent is less than required');
      }
      if (JSON.stringify(tx.message).includes('ERR_LENGTH_MISMATCH')) {
        setValidateText('Incorrect coordinates length');
      }
      if (JSON.stringify(tx.message).includes('ERR_MULTI_MINT_DISABLED')) {
        setValidateText('Minting is disabled');
      }
    }
  };

  useEffect(() => {
    let isMounted = true; // Flag to track if the component is mounted

    const fetchPrice = async (coordinate: { x: number; y: number }) => {
      let price;
      try {
        if (props.mintType === 'eth' || props.mintType === 'weth') {
          price = await fetchLandPrice(coordinate);
        } else if (props.mintType === 'shib') {
          price = await fetchLandShibPrice(coordinate);
        }
      } catch (error) {
        console.error('Error fetching price:', error);
      }
      return price;
    };

    const fetchFee = async (selectedInfo: any[]) => {
      let fee;
      try {
        const feeArray = selectedInfo.map(info => [info.id, true]);
        if (chain?.id === mainNetworkChainId) {
          fee = await landSaleMainnetContract?.getMessageFee(feeArray);
        } else if (chain?.id === sepoliaChainId) {
          fee = await landSaleSeploliaContract?.getMessageFee(feeArray);
        }
      } catch (error) {
        console.error('Error fetching fee:', error);
      }
      return fee;
    };

    const getPriceAndFee = async () => {
      if (!props.isOpen || !props.selectedInfo || props.selectedInfo.length === 0) return;

      setIsLoading(true);
      let totalFee;
      if (chain?.id === mainNetworkChainId || chain?.id === sepoliaChainId) {
        totalFee = await fetchFee(props.selectedInfo);
      }

      let totalPrice;
      const pArray = [] as any;

      for (let i = 0; i < props.selectedInfo.length; i++) {
        const { coordinates } = props.selectedInfo[i];
        const price = await fetchPrice(coordinates);

        if (price === undefined) {
          console.log(`Price undefined, retrying...`);
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait for 1 second before retrying
          i--; // Decrement i to retry the same index
          continue; // Skip to the next iteration of the while loop
        }

        totalPrice = totalPrice ? totalPrice.add(price) : price;
        pArray.push(price);
      }

      if (totalPrice && isMounted) {
        // Check if still mounted before updating state
        setFormattedTotalPrice(ethers.formatEther(totalPrice._hex));
        setTotalPrice(totalPrice);
        if (chain?.id === mainNetworkChainId || chain?.id === sepoliaChainId) {
          setTotalFee(totalFee);
        }
        setPriceArray([...pArray]);
        setIsLoading(false);
      }
    };

    getPriceAndFee();

    return () => {
      isMounted = false; // Cleanup function to set the flag to false
      // Reset states here if needed
      setFormattedTotalPrice(0);
      setTotalPrice(undefined);
      setTotalFee(undefined);
      setPriceArray([]);
      setIsLoading(false);
    };
  }, [props.isOpen, props.selectedInfo, props.mintType]);

  const handleApprove = useCallback(async () => {
    try {
      let result;
      if (props.mintType === 'shib') {
        result = await shibApprove();
      } else if (props.mintType === 'weth') {
        result = await wethApprove();
      }

      if (result) {
        if (result.success && result.txHash) {
          setRequestedApproval(true);
        } else {
          console.log('Approval failed:', result.error || 'Unknown error');
          setRequestedApproval(false); // Reset if approval fails
        }
      } else {
        console.log('No result returned from approval function');
        setRequestedApproval(false); // Reset if no result
      }
    } catch (e) {
      console.log('Unexpected error during approval:', e);
      setRequestedApproval(false); // Reset on error
    }
  }, [shibApprove, wethApprove, setRequestedApproval]);

  return (
    <Modal isOpen={props.isOpen} onDismiss={props.onDismiss} minheight={false} maxheight={80}>
      <UpperSection>
        <CloseIcon onClick={props.onDismiss}>
          <CloseColor />
        </CloseIcon>

        {!isConfirmedTx && !isPending ? (
          <ConnectWalletWrapper className="flex relative flex-col items-center">
            <>
              <HeaderText className="text-3xl text-center text-white">Mint Lands</HeaderText>
              <HeaderDescription className="text-center text-white">
                You are about to mint for Lands.
              </HeaderDescription>
            </>

            <ContentWrapper className="relative">
              <MintWrapper className="flex justify-between w-full text-white">
                <div>Mint Lands</div>
              </MintWrapper>

              <div className="mt-2 text-left text-white">
                {props.selectedInfo &&
                  props.selectedInfo.map(
                    (item: any) => `(${item.coordinates.x}, ${item.coordinates.y}) `
                  )}
              </div>

              <BalanceWrapper className="flex justify-between w-full">
                <span>
                  {isLoading ? (
                    <div className="flex items-center">
                      <Loader stroke="white" />
                      <span style={{ marginLeft: '0.5rem' }}>Calculating Price...</span>
                    </div>
                  ) : (
                    `Total Price: ${props.mintType === 'eth' || props.mintType === 'weth' ? formattedTotalPrice : shortenDouble(Number(formattedTotalPrice), 2)} ${props.mintType === 'eth' ? 'ETH' : props.mintType === 'weth' ? 'WETH' : 'SHIB'}`
                  )}
                </span>

                <span>
                  Current Balance:{' '}
                  {props.mintType === 'eth'
                    ? `${currentBalance} ETH`
                    : props.mintType === 'weth'
                      ? `${shibaBalanceWETHValue} WETH`
                      : `${shortenDouble(Number(shibaBalanceValue), 2)} SHIB`}
                </span>
              </BalanceWrapper>

              <ValidationText className={`text-red-500 ${!validateText ? 'hidden' : ''}`}>
                {validateText}
              </ValidationText>
            </ContentWrapper>

            {props.mintType === 'shib' &&
            (!shibAllowance || Number(shibAllowance) === 0) &&
            !isLoading ? (
              <NormalButton className="px-12" disabled={requestedApproval} onClick={handleApprove}>
                {!requestedApproval ? 'APPROVE' : <Dots>APPROVING</Dots>}
              </NormalButton>
            ) : props.mintType === 'weth' &&
              (!wethAllowance || Number(wethAllowance) === 0) &&
              !isLoading ? (
              <NormalButton className="px-12" disabled={requestedApproval} onClick={handleApprove}>
                {!requestedApproval ? 'APPROVE' : <Dots>APPROVING</Dots>}
              </NormalButton>
            ) : !isLoading ? (
              <NormalButton className="px-12" onClick={handleMint}>
                MINT
              </NormalButton>
            ) : null}
          </ConnectWalletWrapper>
        ) : isPending ? (
          <ConnectWalletWrapper className="flex relative flex-col items-center">
            <div className="my-4 mt-8">
              <ShiberseLoader size="30px" />
            </div>
            <>
              <HeaderText className="text-3xl text-center">Minting in progress</HeaderText>
              <HeaderDescription className="text-center">
                This process can take few minutes.
              </HeaderDescription>
              <HeaderDescription className="mb-8 text-center">
                Once transaction is over, it will be added to your wallet.
              </HeaderDescription>
            </>
          </ConnectWalletWrapper>
        ) : (
          <ConnectWalletWrapper className="flex relative flex-col items-center">
            <>
              <HeaderIcon>
                <Image
                  src={'/images/map/confirmIcon.svg'}
                  width={40}
                  height={40}
                  alt="confirm-icon"
                />
              </HeaderIcon>
              <HeaderText className="text-3xl text-center text-white">
                Minting Transaction Submitted
              </HeaderText>
              <HeaderDescription className="text-center text-white">WOOF!</HeaderDescription>
              <HeaderDescription className="mb-8 text-center text-white">
                Your land minting is in progress.
              </HeaderDescription>
            </>
          </ConnectWalletWrapper>
        )}
      </UpperSection>
    </Modal>
  );
};

export default React.memo(MintModal);
